import { ComponentChildren } from 'preact';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { Dropdown, MenuItem } from '@components/dropdown';
import {
  IcoAnnouncement,
  IcoBook,
  IcoCart,
  IcoDownload,
  IcoFolder,
  IcoGift,
  IcoLibrary,
  IcoMail,
  IcoTag,
  IcoUser,
} from '@components/icons';
import { hasLevel } from 'shared/auth';
import { UserMenu } from '@course/components/nav/user-menu';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { SiteNavLogo } from '@course/components/nav/site-nav-logo';

type Page =
  | 'dashboard'
  | 'settings'
  | 'branding'
  | 'people'
  | 'courses'
  | 'fields'
  | 'terminology'
  | 'instantCourses'
  | 'prices'
  | 'coupons'
  | 'tenants'
  | 'storefront'
  | 'invitations'
  | 'gifts'
  | 'bulkEnroll'
  | 'campaigns'
  | 'changelog'
  | 'emailSchedule'
  | 'emailDelivery'
  | 'meetings';

function AdminLink({
  href,
  children,
  isSelected,
}: {
  children: ComponentChildren;
  href: string;
  isSelected: boolean;
}) {
  return (
    <a
      href={href}
      class={`border-b-2 py-1 -mb-px ${isSelected ? 'border-indigo-600' : 'border-transparent'}`}
    >
      {children}
    </a>
  );
}

const siteSetupPages: Page[] = ['branding', 'terminology', 'fields', 'storefront'];

export function AdminNav({ currentPage, pageTitle }: { currentPage: Page; pageTitle?: string }) {
  const tenant = useCurrentTenant();
  const user = useCurrentUser();

  useDocumentTitle([pageTitle, 'Manage Site']);

  return (
    <header>
      <nav class="p-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <SiteNavLogo href={tenant.isCore ? '/community' : '/'} />
          {tenant.isCore && (
            <a href="/community" class="text-sm text-gray-500 hover:text-gray-700">
              &larr; Community
            </a>
          )}
        </div>
        <UserMenu />
      </nav>
      <nav class="px-8 border-b flex items-end justify-center gap-8 font-medium">
        <AdminLink href="/admin/dashboard" isSelected={currentPage === 'dashboard'}>
          Dashboard
        </AdminLink>

        <AdminLink href="/admin/people" isSelected={currentPage === 'people'}>
          People
        </AdminLink>

        <AdminLink href="/admin/courses" isSelected={currentPage === 'courses'}>
          Courses
        </AdminLink>

        {tenant.isCore && (
          <AdminLink href="/admin/campaigns" isSelected={currentPage === 'campaigns'}>
            Campaigns
          </AdminLink>
        )}

        <AdminLink href="/admin/email-schedule" isSelected={currentPage === 'emailSchedule'}>
          Email schedule
        </AdminLink>

        <AdminLink href="/admin/email-delivery" isSelected={currentPage === 'emailDelivery'}>
          Email delivery
        </AdminLink>

        <AdminLink href="/admin/meetings" isSelected={currentPage === 'meetings'}>
          Calls
        </AdminLink>

        <Dropdown
          renderMenu={() => (
            <div class="p-2 flex flex-col gap-2">
              <MenuItem href="/admin/branding">
                <IcoTag /> <span class="ml-3">Branding</span>
              </MenuItem>
              {tenant.locale === 'en' && (
                <MenuItem href="/admin/terminology">
                  <IcoBook /> <span class="ml-3">Custom Terminology</span>
                </MenuItem>
              )}
              <MenuItem href="/admin/fields">
                <IcoUser /> <span class="ml-3">Profile Fields</span>
              </MenuItem>
              <MenuItem href="/admin/store">
                <IcoCart /> <span class="ml-3">Storefront</span>
              </MenuItem>
            </div>
          )}
          class="items-center"
          triggerClass={`border-b-2 py-1 -mb-px ${
            siteSetupPages.includes(currentPage) ? 'border-indigo-600' : 'border-transparent'
          }`}
        >
          Site setup
        </Dropdown>

        <AdminLink href="/admin/settings" isSelected={currentPage === 'settings'}>
          Settings
        </AdminLink>

        {hasLevel(user, 'superadmin') && (
          <Dropdown
            renderMenu={() => (
              <div class="p-2 flex flex-col gap-2">
                <MenuItem href="/admin/pricing/prod-ruzuku" data-noroute>
                  <IcoFolder />
                  <span class="ml-3">Pricing</span>
                </MenuItem>
                <MenuItem href="/admin/pricing/prod-bundles" data-noroute>
                  <IcoFolder />
                  <span class="ml-3">Instant Courses Pricing</span>
                </MenuItem>
                <MenuItem href="/admin/pricing/prod-calendar" data-noroute>
                  <IcoFolder />
                  <span class="ml-3">Calendar Pricing</span>
                </MenuItem>
                <MenuItem href="/admin/tenants">
                  <IcoLibrary />
                  <span class="ml-3">Tenants</span>
                </MenuItem>
                <MenuItem href="/csv/upsell-bounces.csv" download="upsell-bounces.csv">
                  <IcoDownload /> <span class="ml-3">Upsell Bounces CSV</span>
                </MenuItem>
                <MenuItem href="/csv/customers.csv" download="customers.csv">
                  <IcoDownload /> <span class="ml-3">Customers CSV</span>
                </MenuItem>
                <MenuItem href="/admin/instant-courses">
                  <IcoBook /> <span class="ml-3">Instant Courses</span>
                </MenuItem>
                <MenuItem href="/admin/invitations">
                  <IcoMail /> <span class="ml-3">Invitations</span>
                </MenuItem>
                <MenuItem href="/admin/gifts">
                  <IcoGift /> <span class="ml-3">Gifts</span>
                </MenuItem>
                <MenuItem href="/admin/bulk-enroll">
                  <IcoMail /> <span class="ml-3">Bulk Enrollment</span>
                </MenuItem>
                {tenant.isCore && (
                  <MenuItem href="/admin/changelog">
                    <IcoAnnouncement /> <span class="ml-3">Changelog</span>
                  </MenuItem>
                )}
              </div>
            )}
            class="items-center"
            triggerClass="text-indigo-600 font-medium py-1"
          >
            Admin
          </Dropdown>
        )}
      </nav>
    </header>
  );
}

export function AdminTabWrapper(props: { children: ComponentChildren }) {
  return (
    <div class="admin-area p-8 w-5xl max-w-full mx-auto" data-feedback-section>
      {props.children}
    </div>
  );
}

export function AdminTabHeader(props: { title: ComponentChildren; children?: ComponentChildren }) {
  return (
    <header class="flex items-center justify-between gap-8 mb-8">
      <h2 class="text-2xl">{props.title}</h2>
      {props.children}
    </header>
  );
}
